/**
 * Expense dashboard and reports — read-only aggregates over claims and advances for finance, the
 * Expense Manager, and a reporting manager's own line. Declared on its own (invariant 8) before
 * the API and the admin screen that consume it.
 *
 * WHO SEES WHAT. Every route is `expense:read`, and the POPULATION is the caller's own grants of it
 * expanded exactly as the roster resolver expands them: an org grant (finance, expense_manager)
 * sees everyone, a legal-entity or department grant sees the people placed there, a reporting
 * manager sees their reporting tree. A `self` grant does not count — everyone holds `expense:read`
 * over themselves, and a "dashboard" of one's own claims is the Claims page. A caller with nothing
 * wider gets 403. Expense money is not pay, so ADR 0023's salary visibility is untouched.
 *
 * WHICH CLAIMS. The range is on `expenseDate` (the calendar day the money was spent, invariant 3),
 * both ends inclusive, at most 366 days — the route refuses a wider one with 422 so every query
 * stays bounded. "Spend" is claims that were authorised: `approved` and `settled`. Drafts,
 * withdrawn and rejected claims are never spend; `pending` is reported beside it, not in it.
 *
 * WHERE A CLAIM BELONGS. Department and legal entity are the claimant's placement ON THE EXPENSE
 * DATE (invariant 5 — what March's spend was is answered by March's org chart, not today's). The
 * `legalEntityId` and `departmentId` filters narrow by that same placement. A claim whose claimant
 * had no placement that day falls into a null group ("Unassigned"). WHO is in the population is
 * still decided by today's placement, as `can()` decides it for a single claim.
 *
 * CURRENCY. Amounts are decimal strings (invariant 2) and every row carries its currency. There is
 * no FX facility in this codebase, so nothing is ever converted and two currencies are never
 * added: every aggregate is grouped by currency, and a client shows one total per currency.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  expenseClaimStatusSchema,
  instantSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Query
// ---------------------------------------------------------------------------

/**
 * The filter every report takes. `from`/`to` are required so no report silently means "all time";
 * that `from <= to` and the span is at most 366 days is checked by the route (422), not here, so
 * the copilot's derived tool keeps a plain object schema.
 */
export const expenseReportQuerySchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  legalEntityId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  currency: currencySchema.optional(),
});
export type ExpenseReportQuery = z.infer<typeof expenseReportQuerySchema>;

/** `period` is by calendar month of the expense date (`YYYY-MM`). */
export const EXPENSE_REPORT_DIMENSIONS = [
  'period',
  'department',
  'category',
  'legal_entity',
] as const;
export const expenseReportDimensionSchema = z.enum(EXPENSE_REPORT_DIMENSIONS);
export type ExpenseReportDimension = z.infer<typeof expenseReportDimensionSchema>;

export const expenseSpendBreakdownQuerySchema = expenseReportQuerySchema.extend({
  groupBy: expenseReportDimensionSchema,
});

export const expenseTopSpendersQuerySchema = expenseReportQuerySchema.extend({
  /** Per currency: the top N claimants in each currency. */
  limit: z.coerce.number().int().min(1).max(50).default(10),
});

/** `claims` is one line per claim, `gst` one line per claim carrying a GST split (R-P4 — the
 * merchant-GSTIN/taxable/CGST/SGST/IGST/ITC-eligible columns finance needs for a GSTR-2 filing);
 * the rest are the breakdown by that dimension. */
export const expenseReportExportQuerySchema = expenseReportQuerySchema.extend({
  dataset: z.enum(['claims', 'gst', ...EXPENSE_REPORT_DIMENSIONS]),
});

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/** Days since submission — claims — or since disbursal — advances. */
export const EXPENSE_AGEING_BUCKETS = ['0_7', '8_15', '16_30', '31_60', 'over_60'] as const;
export const expenseAgeingBucketSchema = z.enum(EXPENSE_AGEING_BUCKETS);
export type ExpenseAgeingBucket = z.infer<typeof expenseAgeingBucketSchema>;

/** Whose claims the figures cover: everyone, or the people the caller's narrower grants reach. */
export const expenseReportScopeSchema = z.object({
  kind: z.enum(['org', 'restricted']),
  /** How many people a `restricted` scope reaches; null for `org`. */
  personCount: z.number().int().nonnegative().nullable(),
});

export const expenseSpendSummarySchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  scope: expenseReportScopeSchema,
  /** One row per currency, never summed across them. */
  totals: z.array(
    z.object({
      currency: currencySchema,
      /** approved + settled. */
      spendAmount: decimalStringSchema,
      spendClaimCount: z.number().int().nonnegative(),
      settledAmount: decimalStringSchema,
      pendingAmount: decimalStringSchema,
      pendingClaimCount: z.number().int().nonnegative(),
      claimantCount: z.number().int().nonnegative(),
    }),
  ),
});
export type ExpenseSpendSummary = z.infer<typeof expenseSpendSummarySchema>;

export const expenseSpendBreakdownRowSchema = z.object({
  /** `YYYY-MM` for period, else the department / category / legal entity id; null = Unassigned. */
  key: z.string().nullable(),
  label: z.string(),
  currency: currencySchema,
  amount: decimalStringSchema,
  claimCount: z.number().int().nonnegative(),
});
export type ExpenseSpendBreakdownRow = z.infer<typeof expenseSpendBreakdownRowSchema>;

export const expenseSpendBreakdownSchema = z.object({
  groupBy: expenseReportDimensionSchema,
  /** Ordered by currency, then period ascending or amount descending. */
  rows: z.array(expenseSpendBreakdownRowSchema),
});
export type ExpenseSpendBreakdown = z.infer<typeof expenseSpendBreakdownSchema>;

export const expenseClaimsPipelineSchema = z.object({
  /** Claims with an expense date in the range, by status and currency (drafts excluded). */
  byStatus: z.array(
    z.object({
      status: expenseClaimStatusSchema,
      currency: currencySchema,
      claimCount: z.number().int().nonnegative(),
      amount: decimalStringSchema,
    }),
  ),
  /**
   * The open backlog AS OF NOW, whatever the range: `pending` (awaiting a decision) and `approved`
   * (awaiting payment, counted at the amount still payable after any advance net-off), by days
   * since submission. The entity, department and currency filters still apply.
   */
  ageing: z.array(
    z.object({
      stage: z.enum(['pending', 'approved']),
      bucket: expenseAgeingBucketSchema,
      currency: currencySchema,
      claimCount: z.number().int().nonnegative(),
      amount: decimalStringSchema,
    }),
  ),
});
export type ExpenseClaimsPipeline = z.infer<typeof expenseClaimsPipelineSchema>;

/**
 * Advances disbursed and not yet fully recovered, AS OF NOW (the range does not apply; the entity,
 * department and currency filters do, by the claimant's current placement). Overdue is judged
 * against the caller's local today — one day for the whole query (ADR 0025, lists).
 */
export const expenseOutstandingAdvancesSchema = z.object({
  asOf: dateOnlySchema,
  totals: z.array(
    z.object({
      currency: currencySchema,
      outstandingAmount: decimalStringSchema,
      advanceCount: z.number().int().nonnegative(),
      overdueAmount: decimalStringSchema,
      overdueCount: z.number().int().nonnegative(),
    }),
  ),
  /** By days since disbursal. */
  ageing: z.array(
    z.object({
      bucket: expenseAgeingBucketSchema,
      currency: currencySchema,
      advanceCount: z.number().int().nonnegative(),
      outstandingAmount: decimalStringSchema,
    }),
  ),
});
export type ExpenseOutstandingAdvances = z.infer<typeof expenseOutstandingAdvancesSchema>;

/**
 * Category monthly budgets vs. actual (2026-09-28, gap fix — `ExpenseCategory.monthlyBudget` had a
 * hard-gate enforcement at submit, R-P2's own audit note, but no screen ever showed the number
 * anyone was being blocked against). Mirrors the enforcement in `checkExpenseCategoryCapsForSubmit`
 * exactly: org-wide (no department/entity split — the column itself has no per-entity meaning, see
 * its own schema comment), the current UTC calendar month only, `pending` + `approved` + `settled`
 * claims converted to the org's base currency and summed, skipping a claim in a currency nobody has
 * entered a rate for rather than mis-summing it. Only categories with a budget set are returned.
 *
 * ORG SCOPE ONLY. Since the budget is enforced across every claimant regardless of placement, a
 * `restricted` population (a department or reporting-tree grant) would see a total that includes
 * spend by people outside what it may otherwise see — the route 403s such a caller instead.
 */
export const expenseBudgetVsActualRowSchema = z.object({
  categoryId: uuidSchema,
  categoryName: z.string(),
  currency: currencySchema,
  budgetAmount: decimalStringSchema,
  actualAmount: decimalStringSchema,
  remainingAmount: decimalStringSchema,
  claimCount: z.number().int().nonnegative(),
});
export type ExpenseBudgetVsActualRow = z.infer<typeof expenseBudgetVsActualRowSchema>;

export const expenseBudgetVsActualSchema = z.object({
  /** `YYYY-MM`, the current UTC calendar month. */
  month: z.string(),
  rows: z.array(expenseBudgetVsActualRowSchema),
});
export type ExpenseBudgetVsActual = z.infer<typeof expenseBudgetVsActualSchema>;

export const expenseTopSpendersSchema = z.object({
  rows: z.array(
    z.object({
      currency: currencySchema,
      /** 1-based, within the currency. */
      rank: z.number().int().positive(),
      personId: uuidSchema,
      personName: z.string(),
      departmentName: z.string().nullable(),
      amount: decimalStringSchema,
      claimCount: z.number().int().nonnegative(),
    }),
  ),
});
export type ExpenseTopSpenders = z.infer<typeof expenseTopSpendersSchema>;

/** Returned inline, never stored — same reasoning as payroll-reports' `reportExportSchema`. */
export const expenseReportExportSchema = z.object({
  fileName: z.string(),
  contentType: z.literal('text/csv'),
  content: z.string(),
  rowCount: z.number().int().nonnegative(),
  generatedAt: instantSchema,
});
export type ExpenseReportExport = z.infer<typeof expenseReportExportSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const errors = {
  401: errorBodySchema,
  /** No `expense:read` wider than self. */
  403: errorBodySchema,
  /** `from` after `to`, or a span over 366 days. */
  422: errorBodySchema,
};

export const expenseReportContract = c.router(
  {
    getSpendSummary: {
      method: 'GET',
      path: '/summary',
      query: expenseReportQuerySchema,
      responses: { 200: expenseSpendSummarySchema, ...errors },
      summary: 'Expense spend and pending totals per currency for the people the caller may see',
      metadata: routeMeta('expense:read'),
    },

    getSpendBreakdown: {
      method: 'GET',
      path: '/spend',
      query: expenseSpendBreakdownQuerySchema,
      responses: { 200: expenseSpendBreakdownSchema, ...errors },
      summary: 'Expense spend by month, department, category or legal entity, per currency',
      metadata: routeMeta('expense:read'),
    },

    getClaimsPipeline: {
      method: 'GET',
      path: '/pipeline',
      query: expenseReportQuerySchema,
      responses: { 200: expenseClaimsPipelineSchema, ...errors },
      summary: 'Expense claims by status, and the ageing of the open backlog',
      metadata: routeMeta('expense:read'),
    },

    getOutstandingAdvances: {
      method: 'GET',
      path: '/advances',
      query: expenseReportQuerySchema,
      responses: { 200: expenseOutstandingAdvancesSchema, ...errors },
      summary: 'Outstanding expense advances per currency, overdue and by age',
      metadata: routeMeta('expense:read'),
    },

    getTopSpenders: {
      method: 'GET',
      path: '/top-spenders',
      query: expenseTopSpendersQuerySchema,
      responses: { 200: expenseTopSpendersSchema, ...errors },
      summary: 'The claimants with the highest approved expense spend, per currency',
      metadata: routeMeta('expense:read'),
    },

    /** No query — always the current UTC calendar month, org-wide. 403 for a restricted population
     * (see the schema's own comment on why). */
    getBudgetVsActual: {
      method: 'GET',
      path: '/budgets',
      responses: { 200: expenseBudgetVsActualSchema, ...errors },
      summary: 'This month’s spend against each category’s monthly budget, org-wide',
      metadata: routeMeta('expense:read'),
    },

    /**
     * `ai: 'hidden'`: the response is a whole CSV file. The copilot answers from the aggregate
     * reads above and can navigate the person to the dashboard to download it.
     */
    exportReport: {
      method: 'GET',
      path: '/export',
      query: expenseReportExportQuerySchema,
      responses: { 200: expenseReportExportSchema, ...errors },
      summary: 'Download an expense report as CSV',
      metadata: routeMeta('expense:read', { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/expense/reports' },
);
